import { useEffect, useState } from 'react'
import type { ClipboardEvent, DragEvent, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bug, Lightbulb, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ToastAction } from '@/components/ui/toast'
import { toast } from '@/hooks/use-toast'
import { useCreateFeedback } from '@/hooks/useFeedback'
import { ApiError } from '@/lib/api-error'
import { cn } from '@/lib/utils'
import { MAX_ATTACHMENTS, validateAttachments } from '@/lib/feedback/attachments'
import type { AttachmentError } from '@/lib/feedback/attachments'
import { dataUrlToBlob } from '@/lib/feedback/captureScreenshot'
import { snapshotDiagnostics } from '@/lib/feedback/diagnostics'
import { captureReplayId } from '@/lib/feedback/replay'
import type { FeedbackType } from '@/types/feedback'
import { AttachmentDropzone } from './AttachmentDropzone'
import { ScreenshotEditor } from './ScreenshotEditor'

const TYPES: { value: FeedbackType; icon: typeof Bug }[] = [
  { value: 'bug', icon: Bug },
  { value: 'feature', icon: Lightbulb },
  { value: 'feedback', icon: MessageSquare },
]

const ERROR_KEYS: Record<number, string> = {
  413: 'errors.tooLarge',
  415: 'errors.unsupportedType',
  429: 'errors.rateLimited',
}

// Mirrors PAGE_URL_MAX_LENGTH in app/api/feedback.py.
const PAGE_URL_MAX_LENGTH = 2048

/** True when the drag carries files, not text or a link. */
function carriesFiles(data: DataTransfer | null): boolean {
  return Array.from(data?.types ?? []).includes('Files')
}

function isTextField(target: EventTarget): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  )
}

function errorKey(error: unknown): string {
  if (!(error instanceof ApiError)) return 'errors.generic'
  // A 422 is about the attachment count only when the server says so; it also
  // covers a context over 64 KB or text the database cannot store.
  if (error.status === 422) {
    return /attachments/i.test(error.message) ? 'errors.tooMany' : 'errors.invalid'
  }
  return ERROR_KEYS[error.status] ?? 'errors.generic'
}

interface FeedbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The page as captured when the button was clicked; null if capture failed. */
  screenshot: string | null
}

export function FeedbackDialog({ open, onOpenChange, screenshot }: FeedbackDialogProps) {
  const { t } = useTranslation('feedback')
  const navigate = useNavigate()
  const createFeedback = useCreateFeedback()
  const [type, setType] = useState<FeedbackType>('bug')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [shot, setShot] = useState<string | null>(screenshot)
  const [editing, setEditing] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [fileErrors, setFileErrors] = useState<AttachmentError[]>([])
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  // The screenshot takes one of the five attachment slots.
  const maxFiles = MAX_ATTACHMENTS - (shot ? 1 : 0)
  const canSubmit = title.trim() !== '' && description.trim() !== '' && !sending

  const addFiles = (incoming: File[]) => {
    const { accepted, errors } = validateAttachments(files, incoming, maxFiles)
    setFiles((current) => [...current, ...accepted])
    setFileErrors(errors)
  }

  // A file let go anywhere else (overlay, editor, a field) would make the
  // browser open it in place of the app and throw away what was written.
  useEffect(() => {
    if (!open) return
    const keepFileInPage = (event: globalThis.DragEvent) => {
      if (carriesFiles(event.dataTransfer)) event.preventDefault()
    }
    window.addEventListener('dragover', keepFileInPage)
    window.addEventListener('drop', keepFileInPage)
    return () => {
      window.removeEventListener('dragover', keepFileInPage)
      window.removeEventListener('drop', keepFileInPage)
    }
  }, [open])

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    // The drop zone already took it.
    if (event.defaultPrevented || !carriesFiles(event.dataTransfer)) return
    event.preventDefault()
    if (editing || sending) return
    addFiles(Array.from(event.dataTransfer.files))
  }

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    // Excel, Word and Numbers put an image of the selection next to the text:
    // in a field the user means the text.
    const text = event.clipboardData.getData('text/plain')
    if (isTextField(event.target) && text !== '') return
    const pasted = Array.from(event.clipboardData.files)
    if (pasted.length === 0) return
    event.preventDefault()
    // Every file goes through validation, so a refused type gets its message.
    addFiles(pasted)
  }

  // Closing now would throw away the report being sent or the edits on the
  // screenshot; the explicit close button still works while editing.
  const handleOpenChange = (next: boolean) => {
    if (!next && sending) return
    onOpenChange(next)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setSending(true)
    setSubmitError(null)
    try {
      const form = new FormData()
      form.append('type', type)
      form.append('title', title.trim())
      form.append('description', description.trim())
      form.append('page_url', window.location.href.slice(0, PAGE_URL_MAX_LENGTH))
      form.append('context', JSON.stringify(snapshotDiagnostics()))
      if (type === 'bug') {
        const replayId = await captureReplayId()
        if (replayId) form.append('sentry_replay_id', replayId)
      }
      if (shot) form.append('screenshot', dataUrlToBlob(shot), 'screenshot.png')
      for (const file of files) form.append('files', file)

      const report = await createFeedback.mutateAsync(form)
      onOpenChange(false)
      toast({
        title: t('dialog.sent'),
        description: t('dialog.sentDescription'),
        action: (
          <ToastAction altText={t('dialog.view')} onClick={() => navigate(`/feedback/${report.id}`)}>
            {t('dialog.view')}
          </ToastAction>
        ),
      })
    } catch (error) {
      setSubmitError(t(errorKey(error)))
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-feedback-ignore=""
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        onPaste={handlePaste}
        onDrop={handleDrop}
        onEscapeKeyDown={(event) => {
          if (!editing) return
          event.preventDefault()
          setEditing(false)
        }}
        onInteractOutside={(event) => {
          if (editing) event.preventDefault()
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('dialog.title')}</DialogTitle>
          <DialogDescription>{t('dialog.description')}</DialogDescription>
        </DialogHeader>

        {editing && shot ? (
          <ScreenshotEditor
            src={shot}
            onCancel={() => setEditing(false)}
            onSave={(edited) => {
              setShot(edited)
              setEditing(false)
            }}
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div
              role="radiogroup"
              aria-label={t('dialog.typeLabel')}
              className="grid grid-cols-3 gap-2"
            >
              {TYPES.map(({ value, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={type === value}
                  onClick={() => setType(value)}
                  className={cn(
                    'flex min-w-0 items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-sm transition-colors sm:gap-2 sm:px-3',
                    type === value ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-muted',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {t(`types.${value}`)}
                </button>
              ))}
            </div>

            <div className="space-y-1">
              <Label htmlFor="feedback-title">{t('dialog.titleLabel')}</Label>
              <Input
                id="feedback-title"
                value={title}
                maxLength={120}
                placeholder={t(`dialog.placeholders.${type}.title`)}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="feedback-description">{t('dialog.descriptionLabel')}</Label>
              <Textarea
                id="feedback-description"
                rows={5}
                value={description}
                maxLength={10000}
                placeholder={t(`dialog.placeholders.${type}.description`)}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label>{t('dialog.screenshot')}</Label>
              {shot ? (
                <div className="flex items-start gap-3">
                  <img
                    src={shot}
                    alt={t('dialog.screenshotAlt')}
                    className="h-24 max-w-[12rem] rounded border object-cover object-top"
                  />
                  <div className="flex flex-col gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => setEditing(true)}>
                      {t('dialog.edit')}
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setShot(null)}>
                      {t('dialog.remove')}
                    </Button>
                  </div>
                </div>
              ) : (
                screenshot === null && (
                  <p className="text-sm text-muted-foreground">{t('dialog.screenshotUnavailable')}</p>
                )
              )}
            </div>

            <div className="space-y-1">
              <Label>{t('attachments.label')}</Label>
              <AttachmentDropzone
                files={files}
                maxFiles={maxFiles}
                errors={fileErrors}
                onAdd={addFiles}
                onRemove={(index) => {
                  setFiles((current) => current.filter((_, i) => i !== index))
                  setFileErrors([])
                }}
              />
            </div>

            {type === 'bug' && <p className="text-xs text-muted-foreground">{t('dialog.bugHint')}</p>}

            {submitError && (
              <p role="alert" className="text-sm text-destructive">
                {submitError}
              </p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={sending}
                onClick={() => handleOpenChange(false)}
              >
                {t('dialog.cancel')}
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {sending ? t('dialog.submitting') : t('dialog.submit')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
